"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { WorkspaceSidebarIcon } from "@/app/components/WorkspaceSidebarIcon";
import { useWorkspaceAvatarsPreload } from "@/hooks/useWorkspaceAvatarsPreload";

type WorkspaceSidebarItem = {
  id: string;
  name?: string;
  imageUrl?: string | null;
  tags?: Array<{ key?: string; value?: string }> | null;
};

type WorkspaceSidebarListProps = {
  workspaces: WorkspaceSidebarItem[];
  highlightedWorkspaceId: string | null;
  onSelect: (workspace: WorkspaceSidebarItem) => void;
};

const WORKSPACE_ITEM_SIZE_PX = 40;
const WORKSPACE_ITEM_GAP_PX = 8;
const WORKSPACE_SCROLL_STEP_PX = WORKSPACE_ITEM_SIZE_PX + WORKSPACE_ITEM_GAP_PX;
const WORKSPACE_SIDEBAR_INDICATOR_CLASS =
  "w-1 rounded-br-lg rounded-tr-lg bg-aera-charcoal";

export function WorkspaceSidebarList({
  workspaces,
  highlightedWorkspaceId,
  onSelect,
}: WorkspaceSidebarListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const avatarsReady = useWorkspaceAvatarsPreload(workspaces);

  const activeIndex = workspaces.findIndex(
    (workspace) => workspace.id === highlightedWorkspaceId,
  );
  const hasActive = activeIndex >= 0;

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 2;
    setCanScrollUp(scrollTop > threshold);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - threshold);
  }, []);

  const scrollToWorkspace = useCallback((workspaceId: string) => {
    const item = itemRefs.current.get(workspaceId);
    if (!item) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    item.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, []);

  const scrollByStep = useCallback((direction: "up" | "down") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    container.scrollBy({
      top:
        direction === "down"
          ? WORKSPACE_SCROLL_STEP_PX * 2
          : -WORKSPACE_SCROLL_STEP_PX * 2,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  }, []);

  useEffect(() => {
    if (!highlightedWorkspaceId) return;
    scrollToWorkspace(highlightedWorkspaceId);
  }, [highlightedWorkspaceId, scrollToWorkspace]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    container.addEventListener("scroll", updateScrollState, { passive: true });

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener("scroll", updateScrollState);
    };
  }, [workspaces.length, updateScrollState]);

  return (
    <div className="relative w-full min-h-0 max-h-[calc(100%-3rem)]">
      {!avatarsReady ? (
        <div
          className="flex max-h-full w-full flex-col items-center gap-2 px-3"
          aria-busy="true"
          aria-label="กำลังโหลด workspace"
        >
          {workspaces.map((workspace) => (
            <div
              key={`workspace-skeleton-${workspace.id}`}
              className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-aera-lightgray"
            />
          ))}
        </div>
      ) : (
      <>
      <div
        ref={scrollContainerRef}
        className="relative z-0 flex max-h-full w-full flex-col items-center gap-2 overflow-y-auto overflow-x-hidden scroll-smooth px-3 [--ws-gap:8px] [--ws-item-size:40px] animate-in fade-in duration-200 motion-reduce:animate-none"
        style={
          hasActive
            ? ({ "--ws-active": activeIndex } as CSSProperties)
            : undefined
        }
      >
        {hasActive ? (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 z-20"
          >
            <div
              className={`h-10 ${WORKSPACE_SIDEBAR_INDICATOR_CLASS} transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none`}
              style={{
                transform: `translateY(calc(var(--ws-active) * (${WORKSPACE_ITEM_SIZE_PX}px + ${WORKSPACE_ITEM_GAP_PX}px)))`,
              }}
            />
          </div>
        ) : null}

        {workspaces.map((workspace) => {
          const isActive = highlightedWorkspaceId === workspace.id;

          return (
            <AeraButton
              key={`workspace-${workspace.id}`}
              ref={(node) => {
                if (node) {
                  itemRefs.current.set(workspace.id, node);
                  return;
                }
                itemRefs.current.delete(workspace.id);
              }}
              variant="ghost"
              paddingSize="none"
              aria-label={workspace.name}
              onClick={() => {
                scrollToWorkspace(workspace.id);
                onSelect(workspace);
              }}
              className={`group relative z-10 h-10 min-h-10 w-10 min-w-10 shrink-0 rounded-xl transition-colors duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none ${
                isActive
                  ? "bg-aera-charcoal hover:bg-aera-charcoal"
                  : "bg-aera-lightgray hover:bg-[#e8e8e8]"
              }`}
            >
              {!isActive ? (
                <span
                  aria-hidden
                  className={`pointer-events-none absolute -left-3 top-1/2 z-20 h-5 -translate-y-1/2 scale-y-0 opacity-0 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-y-100 group-hover:opacity-100 motion-reduce:transition-none ${WORKSPACE_SIDEBAR_INDICATOR_CLASS}`}
                />
              ) : null}
              <WorkspaceSidebarIcon
                workspace={workspace}
                active={isActive}
              />
            </AeraButton>
          );
        })}
      </div>

      {canScrollUp ? (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 flex justify-center bg-linear-to-b from-aera-offwhite via-aera-offwhite/80 to-transparent pb-3 pt-0.5">
          <AeraButton
            type="button"
            variant="transparent"
            paddingSize="none"
            onClick={() => scrollByStep("up")}
            aria-label="เลื่อน workspace ขึ้น"
            className="pointer-events-auto flex h-5 w-10 items-center justify-center rounded-md text-aera-gray hover:text-aera-charcoal"
          >
            <ChevronUp size={18} strokeWidth={3} aria-hidden />
          </AeraButton>
        </div>
      ) : null}

      {canScrollDown ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex justify-center bg-linear-to-t from-aera-offwhite via-aera-offwhite/80 to-transparent pb-0.5 pt-3">
          <AeraButton
            type="button"
            variant="transparent"
            paddingSize="none"
            onClick={() => scrollByStep("down")}
            aria-label="เลื่อน workspace ลง"
            className="pointer-events-auto flex h-5 w-10 items-center justify-center rounded-md text-aera-gray hover:text-aera-charcoal"
          >
            <ChevronDown size={18} strokeWidth={3} aria-hidden />
          </AeraButton>
        </div>
      ) : null}
      </>
      )}
    </div>
  );
}
